/**
 * Code suggestion + validation for the SKU code tables (Phase 65 / BLU-594 PR2,
 * frontend-only). Mirrors the backend's `CODE_RE` (`^[A-Z0-9]{1,6}$`, sizes and
 * colours) and `PRODUCT_TYPE_CODE_RE` (`^[A-Z0-9]{1,10}$`, product types) so a
 * suggestion the UI pre-fills always passes the write-path zod, and the
 * inline-edit validator matches the server contract exactly.
 */
import type { CodeTable } from './types';

/** Matches the shared `CODE_RE` write-path contract for sizes and colours (1-6 alphanumerics). */
export const CODE_PATTERN = /^[A-Za-z0-9]{1,6}$/;

/** Matches the shared `PRODUCT_TYPE_CODE_RE` write-path contract (1-10 alphanumerics). */
export const PRODUCT_TYPE_CODE_PATTERN = /^[A-Za-z0-9]{1,10}$/;

/** True when a code string would pass the write-path validation for its table (case-insensitive input). */
export function isValidCode(code: string, table: CodeTable): boolean {
  return (table === 'productTypes' ? PRODUCT_TYPE_CODE_PATTERN : CODE_PATTERN).test(code.trim());
}

/**
 * Suggest a short code from a value NAME using an uppercase-initials heuristic,
 * capped at 4 characters. Multi-word names take each word's first letter
 * ("Washed Indigo" → "WI"); single-word names take the first characters
 * ("Terracotta" → "TERR"). Returns "" for a name with no alphanumerics so the
 * caller shows a blank input rather than a bogus code.
 */
export function suggestCode(name: string): string {
  const words = name
    .trim()
    .toUpperCase()
    .split(/\s+/)
    .map((w) => w.replace(/[^A-Z0-9]/g, ''))
    .filter((w) => w.length > 0);

  if (words.length === 0) return '';

  if (words.length >= 2) {
    // First letter of up to the first four words.
    return words
      .slice(0, 4)
      .map((w) => w[0])
      .join('');
  }

  return words[0]!.slice(0, 4);
}
